import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { Align, FontType } from "../../../common/TextUtils"
import { StudentStoreSA } from "../../../react/students/StudentStoreSA"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { Gender } from "../../../shared/models/Gender"
import { ButtonContainer, Buttons, ButtonType } from "../../generic-sprites/Buttons"
import { DropDownMenu } from "../../generic-sprites/DropDownMenu"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { Rectangle } from "../../generic-sprites/Shape"
import { Audio, AudioContainer } from "../../utils/Audio"
import { colorNum, ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale, x, y } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { loadAssets, MakeFriendAssets, MakeFriendAudio, MakeFriendImages } from "./MakeFriendAssets"
import { MakeFriendProgressStore } from "./MakeFriendProgressStore"
import { MakeFriendStage } from "./MakeFriendStage"
import {
    Attributes, MakeFriendFifthAttribute, MakeFriendFirstAttribute, MakeFriendFourthAttribute, MakeFriendSecondAttribute, MakeFriendThirdAttribute, StageTypes
} from "./MakeFriendTypes"
import { CharacterContext, CharacterFlavor, CharacterType, MakeFriendCharacter } from "./sprites/MakeFriendCharacter"


enum ActivityStates {
    START = "start",
    INTRO = "intro",
    ATTRIBUTE_SELECTION = "attribute-selection",
    PAPER_DOLL_SELECTION = "paper-doll-selection",
    SPLIT_PAPER_DOLL = "split-paper-doll",
    CHARACTER_SELECTION = "character-selection",
    STAGE_SELECTION = "stage-selection"
}

export class MakeFriendState extends Phaser.State {

    activityState: ActivityStates

    introPaperDollGroup: Phaser.Group
    introTextGroup: Phaser.Group
    attributeSelectionGroup: Phaser.Group
    paperDollSelectionGroup: Phaser.Group
    characterCreationGroup: Phaser.Group
    characterCreationGroupYou: Phaser.Group
    characterCreationGroupFriend: Phaser.Group
    characterCreationSelectArrowsGroup: Phaser.Group
    stageSelectionGroup: Phaser.Group

    username: string
    nameTagBoy
    nameTagBoyRectangle: Rectangle
    nameTagGirl
    nameTagGirlRectangle: Rectangle
    youNameTag
    youNameTagRectangle: Rectangle
    friendNameTag
    friendNameTagRectangle: Rectangle
    paperDollBoy: Phaser.Sprite
    paperDollGirl: Phaser.Sprite
    youCharacter: Phaser.Sprite
    currentlySelectedYouCharacterFlavor: CharacterFlavor
    friendCharacter: Phaser.Sprite
    currentlySelectedFriendCharacterFlavor: CharacterFlavor

    paperDollStartX: number
    friendPaperDollSplitX: number

    nextButton?: ButtonContainer

    howISoundAudio?: AudioContainer

    preload() {
        loadAssets(this.game)
        loadGenericAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        if (!PhaserUtils.loggedIn()) {
            PhaserUtils.notLoggedInMessage(this.game)
            return
        }

        log.info(`Starting make friend. In debug? ${CCUtils.debug}`)

        // Groups
        this.introPaperDollGroup = this.game.add.group()
        this.introTextGroup = this.game.add.group()
        this.attributeSelectionGroup = this.game.add.group()
        this.paperDollSelectionGroup = this.game.add.group()
        this.characterCreationGroup = this.game.add.group()
        this.characterCreationGroupYou = this.game.add.group()
        this.characterCreationGroupFriend = this.game.add.group()
        this.characterCreationSelectArrowsGroup = this.game.add.group()
        this.stageSelectionGroup = this.game.add.group()

        this.paperDollStartX = 0.05
        this.friendPaperDollSplitX = 0.58

        this.username = StudentStoreSA.instance.findUsername()
        if (!this.username) {
            this.username = "Unknown"
        }

        const completedArcade = MakeFriendProgressStore.instance.arcadeStageCompleted
        const completedSleepover = MakeFriendProgressStore.instance.sleepoverStageCompleted
        const completedPool = MakeFriendProgressStore.instance.poolStageCompleted

        /////
        //
        // Set activityState to ActivityStates.LOBBY to skip the Lobby and go straight to the Hallway
        //
        /////
        // If we haven't been to any of the stages, we're starting. Otherwise, we should go to stage selection.
        if (!completedArcade && !completedSleepover && !completedPool) {
            this.activityState = ActivityStates.START
            // this.activityState = ActivityStates.INTRO
            // this.activityState = ActivityStates.ATTRIBUTE_SELECTION
            // this.activityState = ActivityStates.PAPER_DOLL_SELECTION
            // this.activityState = ActivityStates.SPLIT_PAPER_DOLL
            // this.activityState = ActivityStates.CHARACTER_SELECTION
        } else {
            this.activityState = ActivityStates.CHARACTER_SELECTION
        }

        ColorUtils.setBackgroundColor(this.game, "#c2ffff")
        this.game.stage.backgroundColor = "#c2ffff"

        this.progressToNextPart()
    }

    makeNextButtonVisible = () => {
        this.nextButton = Buttons.create(
            this.game,
            "Next",
            () => {
                this.nextButton.enable(false)
                this.nextButton.setInvisible()
                this.progressToNextPart()
            },
            {x: x(0.86), y: y(0.9), width: 0.14, enabled: false, buttonType: ButtonType.PRIMARY}
        )
    }

    turnOnNextButton = () => {
        if (this.nextButton == null || !this.nextButton.button.exists) {
            this.makeNextButtonVisible()
        }
        this.nextButton.setVisible()
        this.nextButton.enable(true)
    }

    // Activity progression
    progressToNextPart = () => {
        switch (this.activityState) {
            case ActivityStates.START:
                this.startIntro()
                this.activityState = ActivityStates.INTRO
                break
            case ActivityStates.INTRO:
                this.startAttributeSelection()
                this.activityState = ActivityStates.ATTRIBUTE_SELECTION
                break
            case ActivityStates.ATTRIBUTE_SELECTION:
                this.startPaperDollSelection()
                this.activityState = ActivityStates.PAPER_DOLL_SELECTION
                break
            case ActivityStates.PAPER_DOLL_SELECTION:
                this.startSplitPaperDoll()
                this.activityState = ActivityStates.SPLIT_PAPER_DOLL
                break
            case ActivityStates.SPLIT_PAPER_DOLL:
                this.startCharacterSelection()
                this.activityState = ActivityStates.CHARACTER_SELECTION
                break
            case ActivityStates.CHARACTER_SELECTION:
                this.showStageSelection()
                this.activityState = ActivityStates.STAGE_SELECTION
                break
            case ActivityStates.STAGE_SELECTION:
                MakeFriendProgressStore.destroy()
                Transitions.startOutro(this.game, ActivityKey.MAKE_A_FRIEND)
                break
            default:
                throw new Error(`Unknown ActivityState: ${this.activityState}`)
        }
    }

    goToArcade = () => {
        this.stageSelectionGroup.destroy()
        new MakeFriendStage(this.game, StageTypes.ARCADE)
    }

    goToSleepover = () => {
        this.stageSelectionGroup.destroy()
        new MakeFriendStage(this.game, StageTypes.SLEEPOVER)
    }

    goToPool = () => {
        this.stageSelectionGroup.destroy()
        new MakeFriendStage(this.game, StageTypes.POOL)
    }

    addPaperdolls() {
        this.paperDollBoy = Sprites.add(this.game, MakeFriendImages.PAPERDOLL_BOY, {
            x: Scale.x(0.1), y: Scale.y(0.2), scale: 1, group: this.introPaperDollGroup
        })
        this.nameTagBoyRectangle = new Rectangle(this.game, 0, 0, x(.2), y(.05), {
            fillColor: colorNum.white, lineWidth: 1, cornerRounding: 5, group: this.introPaperDollGroup
        })
        this.nameTagBoy = PhaserText.create(this.game, this.username, {
            align: Align.CENTER, type: FontType.HEADER, y: Scale.y(0.1), group: this.introPaperDollGroup
        })
        this.nameTagBoy.centerX = this.paperDollBoy.centerX
        this.nameTagBoyRectangle.graphics.centerX = this.nameTagBoy.centerX
        this.nameTagBoyRectangle.graphics.centerY = this.nameTagBoy.centerY

        this.paperDollGirl = Sprites.add(this.game, MakeFriendImages.PAPERDOLL_GIRL, {
            x: Scale.x(0.6), y: Scale.y(0.19), scale: 1, group: this.introPaperDollGroup
        })
        this.nameTagGirlRectangle = new Rectangle(this.game, 0, 0, x(.2), y(.05), {
            fillColor: colorNum.white, lineWidth: 1, cornerRounding: 5, group: this.introPaperDollGroup
        })
        this.nameTagGirl = PhaserText.create(this.game, this.username, {
            align: Align.CENTER, type: FontType.HEADER, y: Scale.y(0.1), group: this.introPaperDollGroup
        })
        this.nameTagGirl.centerX = this.paperDollGirl.centerX
        this.nameTagGirlRectangle.graphics.centerX = this.nameTagGirl.centerX
        this.nameTagGirlRectangle.graphics.centerY = this.nameTagGirl.centerY

        // Boy paper doll nametag starts hidden
        this.nameTagBoy.alpha = 0
        this.nameTagBoyRectangle.graphics.alpha = 0

        // Girl paper doll starts hidden
        this.paperDollGirl.alpha = 0
        this.nameTagGirlRectangle.graphics.alpha = 0
        this.nameTagGirl.alpha = 0
    }

    addGenderedPaperDollsWithAttributes(startSplit = false) {
        let paperDollImage = MakeFriendImages.PAPERDOLL_GIRL
        if (MakeFriendProgressStore.isBoy()) {
            paperDollImage = MakeFriendImages.PAPERDOLL_BOY
        }

        const characterY = y(.11)

        this.friendCharacter = Sprites.add(this.game, paperDollImage, {
            y: characterY, scale: 1.1, group: this.characterCreationGroupFriend
        })
        let friendX
        if (startSplit) {
            friendX = x(this.friendPaperDollSplitX)
        } else {
            friendX = x(this.paperDollStartX)
        }
        this.friendCharacter.x = friendX
        this.friendNameTagRectangle = new Rectangle(this.game, 0, 0, x(.2), y(.05), {
            fillColor: colorNum.white, lineWidth: 1, cornerRounding: 5, group: this.characterCreationGroup
        })
        this.friendNameTag = PhaserText.create(this.game, MakeFriendProgressStore.getFriendName, {
            align: Align.CENTER, type: FontType.HEADER, x: x(.725), y: Scale.y(0.02), group: this.characterCreationGroup
        })
        this.friendNameTagRectangle.graphics.centerX = this.friendNameTag.centerX
        this.friendNameTagRectangle.graphics.centerY = this.friendNameTag.centerY
        this.friendNameTagRectangle.graphics.alpha = 0
        this.friendNameTag.alpha = 0

        this.youCharacter = Sprites.add(this.game, paperDollImage, {
            x: x(this.paperDollStartX), y: characterY, scale: 1.1, group: this.characterCreationGroupYou
        })
        this.youNameTagRectangle = new Rectangle(this.game, 0, 0, x(.2), y(.05), {
            fillColor: colorNum.white, lineWidth: 1, cornerRounding: 5, group: this.characterCreationGroup
        })
        this.youNameTag = PhaserText.create(this.game, this.username, {
            align: Align.CENTER, type: FontType.HEADER, x: x(.122), y: y(0.02), group: this.characterCreationGroup
        })
        this.youNameTag.centerX = this.youCharacter.centerX
        this.youNameTagRectangle.graphics.centerX = this.youNameTag.centerX
        this.youNameTagRectangle.graphics.centerY = this.youNameTag.centerY

        // You Paperdoll Attributes
        let yValue = .35
        MakeFriendProgressStore.allAttributes.forEach((attribute) => {
            yValue += .03
            const attributeText = PhaserText.create(this.game, attribute,
                {y: y(yValue), fontPx: 12, centerInWorldXOnly: true, group: this.characterCreationGroupYou})
            attributeText.centerX = this.youCharacter.centerX
        })

        // Friend Paperdoll Attributes
        yValue = .35  // reset
        MakeFriendProgressStore.allAttributes.forEach((attribute) => {
            yValue += .03
            const attributeText = PhaserText.create(this.game, attribute,
                {y: y(yValue), fontPx: 12, centerInWorldXOnly: true, group: this.characterCreationGroupFriend})
            attributeText.centerX = this.friendCharacter.centerX
        })
    }

    debugSetAttributes() {
        if (CCUtils.debug) {
            MakeFriendProgressStore.instance.setFirstAttribute(MakeFriendFirstAttribute.TALKATIVE)
            MakeFriendProgressStore.instance.setSecondAttribute(MakeFriendSecondAttribute.GENEROUS)
            MakeFriendProgressStore.instance.setThirdAttribute(MakeFriendThirdAttribute.HARDWORKING)
            MakeFriendProgressStore.instance.setFourthAttribute(MakeFriendFourthAttribute.EASYGOING)
            MakeFriendProgressStore.instance.setFifthAttribute(MakeFriendFifthAttribute.ARTISTIC)
        }
    }

    debugSetGender(gender: Gender = Gender.GIRL) {
        if (CCUtils.debug) {
            MakeFriendProgressStore.instance.setGender(gender)
        }
    }

    debugCreatePaperDollsWithAttributes() {
        if (CCUtils.debug) {
            this.addGenderedPaperDollsWithAttributes(true)
            this.friendCharacter.alpha = 1
            this.friendNameTagRectangle.graphics.alpha = 1
            this.friendNameTag.alpha = 1
        }
    }

    startIntro() {
        this.addPaperdolls()

        const title = PhaserText.create(this.game, "Make a Friend", {x: x(.55), y: y(.25), color: COLOR.red, group: this.introTextGroup})
        const body = PhaserText.create(this.game,
            "In this activity, you and your new friend will be in three different situations where you’ll share your thoughts about kids who vape or smoke. But first, we need to get some information about you so the computer can create a friend with a personality similar to yours.",
            {y: y(.33), wordWrapWidth: .5, group: this.introTextGroup, align: Align.CENTER})
        body.centerX = title.centerX


        Audio.add(this.game, MakeFriendAudio.INTRO, {
            onStop: this.turnOnNextButton
        })
    }

    startAttributeSelection() {
        this.introTextGroup.destroy()

        // For starting midway (debug)
        if (CCUtils.debug && !this.paperDollBoy) {
            this.addPaperdolls()
        }

        this.nameTagBoy.alpha = 1
        this.nameTagBoyRectangle.graphics.alpha = 1

        PhaserText.create(this.game, "I am:", {x: x(.65), y: y(.18), group: this.attributeSelectionGroup})

        new Rectangle(this.game, x(.52), y(.14), x(.34), y(.68), {
            fillColor: colorNum.white, lineWidth: 1, cornerRounding: 5, group: this.attributeSelectionGroup
        })
        this.makeNextButtonVisible()
        let yValue = .20
        let selectionNum = 0
        const attributeDropdowns = []
        Attributes.forEach((attributeGroup, groupNumber: 0 | 1 | 2 | 3 | 4) => {
            yValue += .10
            selectionNum += 1
            const dropdown = new DropDownMenu(this.game, attributeGroup, {
                x: x(0.60),
                y: y(yValue),
                placeholder: "- Choose one -",
                blankNum: selectionNum as 1 | 2 | 3 | 4 | 5,
                group: this.attributeSelectionGroup,
                startEnabled: false,
                onSelect: (attribute) => {
                    switch (groupNumber) {
                        case 0:
                            MakeFriendProgressStore.instance.setFirstAttribute(attribute as MakeFriendFirstAttribute)
                            break
                        case 1:
                            MakeFriendProgressStore.instance.setSecondAttribute(attribute as MakeFriendSecondAttribute)
                            break
                        case 2:
                            MakeFriendProgressStore.instance.setThirdAttribute(attribute as MakeFriendThirdAttribute)
                            break
                        case 3:
                            MakeFriendProgressStore.instance.setFourthAttribute(attribute as MakeFriendFourthAttribute)
                            break
                        case 4:
                            MakeFriendProgressStore.instance.setFifthAttribute(attribute as MakeFriendFifthAttribute)
                            break
                    }
                    if (MakeFriendProgressStore.allAttributesSet) {
                        this.turnOnNextButton()
                    }
                }
            })
            attributeDropdowns.push(dropdown)
        })

        Audio.add(this.game, MakeFriendAudio.PICK_FIVE_WORDS, {
            onStop: () => attributeDropdowns.forEach(dropdown => dropdown.enable())
        })
    }

    startPaperDollSelection() {
        this.attributeSelectionGroup.destroy()

        // For starting midway (debug)
        if (CCUtils.debug && !this.paperDollBoy) {
            this.addPaperdolls()
            this.debugSetAttributes()
        }

        this.nameTagBoy.alpha = 0
        this.nameTagBoyRectangle.graphics.alpha = 0
        this.paperDollGirl.alpha = 1

        this.makeNextButtonVisible()

        this.paperDollBoy.inputEnabled = true
        this.paperDollBoy.events.onInputDown.add(() => {
            this.nameTagGirl.alpha = 0
            this.nameTagGirlRectangle.graphics.alpha = 0
            this.nameTagBoy.alpha = 1
            this.nameTagBoyRectangle.graphics.alpha = 1
            MakeFriendProgressStore.instance.setGender(Gender.BOY)
            this.turnOnNextButton()
        })

        this.paperDollGirl.inputEnabled = true
        this.paperDollGirl.events.onInputDown.add(() => {
            this.nameTagBoy.alpha = 0
            this.nameTagBoyRectangle.graphics.alpha = 0
            this.nameTagGirl.alpha = 1
            this.nameTagGirlRectangle.graphics.alpha = 1
            MakeFriendProgressStore.instance.setGender(Gender.GIRL)
            this.turnOnNextButton()
        })

        Audio.add(this.game, MakeFriendAudio.CHOOSE_YOUR_VIRTUAL_SELF)
    }

    startSplitPaperDoll() {
        this.paperDollSelectionGroup.destroy()
        this.introPaperDollGroup.destroy()

        // For starting midway (debug)
        if (CCUtils.debug && !MakeFriendProgressStore.getGender) {
            this.debugSetAttributes()
            this.debugSetGender()
        }

        let heresNewFriendAudio = MakeFriendAudio.HERES_YOUR_NEW_FRIEND_SARAH
        if (MakeFriendProgressStore.isBoy()) {
            heresNewFriendAudio = MakeFriendAudio.HERES_YOUR_NEW_FRIEND_BEN
        }

        this.addGenderedPaperDollsWithAttributes()

        Audio.add(this.game, MakeFriendAudio.BASED_ON_THE_PERSONALITY_CHARACTERISTICS, {
            // Disallow selection while audio playing?
            onStop: () => {
                const newFriendAudio = this.game.add.audio(heresNewFriendAudio)
                newFriendAudio.play()
                newFriendAudio.onStop.addOnce(() => this.progressToNextPart())
                this.friendCharacter.alpha = 1
                const tween = this.game.add.tween(this.characterCreationGroupFriend).to({x: x(this.friendPaperDollSplitX - .05)},
                    1000, Phaser.Easing.Linear.None, true)
                tween.onComplete.add(() => {
                    this.friendNameTagRectangle.graphics.alpha = 1
                    this.friendNameTag.alpha = 1
                })
            }
        })
    }

    startCharacterSelection() {
        // For starting midway (debug)
        if (CCUtils.debug && !MakeFriendProgressStore.getGender) {
            this.debugSetAttributes()
            this.debugSetGender()
            this.debugCreatePaperDollsWithAttributes()
        }

        let hasSelectedYou = false
        let hasSelectedFriend = false

        const selectYouCharacter = () => {
            this.characterCreationGroupYou.destroy()
            hasSelectedYou = true
            if (hasSelectedFriend) {
                this.turnOnNextButton()
            }
            let newFlavor: CharacterFlavor
            if (!this.currentlySelectedYouCharacterFlavor) {
                newFlavor = CharacterFlavor.ONE
            } else {
                switch (this.currentlySelectedYouCharacterFlavor) {
                    case CharacterFlavor.ONE:
                        newFlavor = CharacterFlavor.TWO
                        break
                    case CharacterFlavor.TWO:
                        newFlavor = CharacterFlavor.THREE
                        break
                    case CharacterFlavor.THREE:
                        newFlavor = CharacterFlavor.ONE
                        break
                }
            }
            this.currentlySelectedYouCharacterFlavor = newFlavor
            MakeFriendProgressStore.instance.setYouCharacterFlavor(newFlavor)
            playSelectedAvatarAudio(newFlavor)

            this.youCharacter.destroy()
            let yPlacement: number
            if (MakeFriendProgressStore.isBoy()) {
                yPlacement = y(.09)
            } else {
                yPlacement = y(.145)
            }
            this.youCharacter = MakeFriendCharacter.add(this.game, CharacterType.YOU, this.currentlySelectedYouCharacterFlavor, CharacterContext.INTRO,
                {x: x(.066), y: yPlacement, scale: 1, group: this.characterCreationGroup})
        }

        const playSelectedAvatarAudio = (flavor: CharacterFlavor) => {
            const audio = MakeFriendAssets.getAvatarSampleAudio(MakeFriendProgressStore.getGender, flavor)
            if (this.howISoundAudio != null) {
                this.howISoundAudio.audio.stop()
            }
            this.howISoundAudio = Audio.add(this.game, audio)
        }

        const selectFriendCharacter = () => {
            this.characterCreationGroupFriend.destroy()
            hasSelectedFriend = true
            if (hasSelectedYou) {
                this.turnOnNextButton()
            }
            let newFlavor: CharacterFlavor
            if (!this.currentlySelectedFriendCharacterFlavor) {
                newFlavor = CharacterFlavor.ONE
            } else {
                switch (this.currentlySelectedFriendCharacterFlavor) {
                    case CharacterFlavor.ONE:
                        newFlavor = CharacterFlavor.TWO
                        break
                    case CharacterFlavor.TWO:
                        newFlavor = CharacterFlavor.THREE
                        break
                    case CharacterFlavor.THREE:
                        newFlavor = CharacterFlavor.ONE
                        break
                }
            }
            this.currentlySelectedFriendCharacterFlavor = newFlavor
            MakeFriendProgressStore.instance.setFriendCharacterFlavor(newFlavor)

            this.friendCharacter.destroy()
            this.friendCharacter = MakeFriendCharacter.add(this.game, CharacterType.FRIEND, this.currentlySelectedFriendCharacterFlavor, CharacterContext.INTRO,
                {x: x(.582), y: y(.127), scale: 1, group: this.characterCreationGroup})
        }

        const youArrow = Sprites.add(this.game, MakeFriendImages.ARROW_LEFT, {x: x(.32), y: y(.3), group: this.characterCreationSelectArrowsGroup})
        Scale.scaleSprite(youArrow, .15)
        const youText = PhaserText.create(this.game, "Select your look and voice", {fontPx: 18, group: this.characterCreationSelectArrowsGroup})
        youText.x = youArrow.x + youArrow.width + x(.01)
        youText.centerY = youArrow.centerY
        const youRectangle = new Rectangle(
            this.game,
            x(.3),
            y(.28),
            x(.37),
            y(.1),
            {group: this.characterCreationGroup}
        )
        youRectangle.graphics.alpha = 0

        const friendArrow = Sprites.add(this.game, MakeFriendImages.ARROW_RIGHT, {x: x(.6), y: y(.75), group: this.characterCreationSelectArrowsGroup})
        Scale.scaleSprite(friendArrow, .15)
        const friendText = PhaserText.create(this.game, "Select your friend's look", {fontPx: 18, group: this.characterCreationSelectArrowsGroup})
        friendText.x = friendArrow.x - friendText.width - x(.01)
        friendText.centerY = friendArrow.centerY
        const friendRectangle = new Rectangle(
            this.game,
            x(.32),
            y(.72),
            x(.35),
            y(.1),
            {group: this.characterCreationGroup}
        )
        friendRectangle.graphics.alpha = 0

        this.game.world.bringToTop(this.characterCreationSelectArrowsGroup)

        this.makeNextButtonVisible()

        const chooseAppearanceAudio = this.game.add.audio(MakeFriendAudio.CHOOSE_YOUR_APPEARANCE)
        chooseAppearanceAudio.onStop.addOnce(() => {
            Buttons.makeSpriteButton(youRectangle.invisibleSprite, selectYouCharacter)
            Buttons.makeSpriteButton(friendRectangle.invisibleSprite, selectFriendCharacter)
        })
        chooseAppearanceAudio.play()

    }

    showStageSelection() {
        this.characterCreationGroup.destroy()
        this.characterCreationSelectArrowsGroup.destroy()

        // For starting midway (debug)
        if (CCUtils.debug && !MakeFriendProgressStore.getYouCharacterFlavor) {
            this.debugSetGender(Gender.BOY)
            MakeFriendProgressStore.instance.setYouCharacterFlavor(CharacterFlavor.TWO)
            MakeFriendProgressStore.instance.setFriendCharacterFlavor(CharacterFlavor.ONE)
        }

        const stageButtonsInitialYValue = y(.3)
        const stageButtonsEndYValue = y(.46)

        // Uncomment to skip directly to one of the stages
        // this.goToArcade(); return;
        // this.goToSleepover(); return;
        // this.goToPool(); return;

        // Uncomment to mark any of the stages as complete
        // MakeFriendProgressStore.instance.addStage(MakeFriendStages.ARCADE)
        // MakeFriendProgressStore.instance.addStage(MakeFriendStages.SLEEPOVER)
        // MakeFriendProgressStore.instance.addStage(MakeFriendStages.POOL)

        const startText = PhaserText.create(this.game, "What do you and your friend want to do?",
            {y: y(.15), centerInWorldXOnly: true, group: this.stageSelectionGroup})

        const completedArcade = MakeFriendProgressStore.instance.arcadeStageCompleted
        const completedSleepover = MakeFriendProgressStore.instance.sleepoverStageCompleted
        const completedPool = MakeFriendProgressStore.instance.poolStageCompleted

        const arcadeButton = Sprites.add(this.game, MakeFriendImages.STAGE_ARCADE, {scale: 1, y: stageButtonsInitialYValue, group: this.stageSelectionGroup})
        if (completedArcade) {
            arcadeButton.alpha = .4
        } else {
            arcadeButton.events.onInputDown.add(() => this.goToArcade())
        }

        const sleepoverButton = Sprites.add(this.game, MakeFriendImages.STAGE_HOUSE, {scale: 1, y: stageButtonsInitialYValue, group: this.stageSelectionGroup})
        Sprites.centerInWorldXOnly(sleepoverButton)
        if (completedSleepover) {
            sleepoverButton.alpha = .4
        } else {
            sleepoverButton.events.onInputDown.add(() => this.goToSleepover())
        }

        const poolButton = Sprites.add(this.game, MakeFriendImages.STAGE_POOL, {scale: 1, y: stageButtonsInitialYValue, group: this.stageSelectionGroup})
        poolButton.x = Scale.width() - poolButton.width
        if (completedPool) {
            poolButton.alpha = .4
        } else {
            poolButton.events.onInputDown.add(() => this.goToPool())
        }

        const numCompletedStages = [completedArcade, completedSleepover, completedPool].filter(Boolean).length
        let isFirstStage = false
        let nextStageAudioFile
        switch (numCompletedStages) {
            case 0:
                isFirstStage = true
                nextStageAudioFile = MakeFriendAudio.FIRST_STAGE_SELECTION
                break
            case 1:
                nextStageAudioFile = MakeFriendAudio.SECOND_STAGE_SELECTION
                break
            case 2:
                nextStageAudioFile = MakeFriendAudio.THIRD_STAGE_SELECTION
                break
            default:
                // Assume we are done otherwise
                nextStageAudioFile = MakeFriendAudio.OUTRO
                startText.alpha = 0
                PhaserText.create(
                    this.game,
                    "You discovered that you and your friend think vapers and smokers are not cool, exciting, or popular. Most kids don’t want to hang out with people when they’re vaping or smoking!",
                    {fontPx: 18, y: y(.2), align: Align.CENTER, wordWrapWidth: .7, centerInWorldXOnly: true, group: this.stageSelectionGroup}
                )
                arcadeButton.y = stageButtonsEndYValue
                sleepoverButton.y = stageButtonsEndYValue
                poolButton.y = stageButtonsEndYValue

                const characterHorizontalSpacing = x(.02)
                MakeFriendCharacter.add(this.game, CharacterType.YOU, MakeFriendProgressStore.getYouCharacterFlavor, CharacterContext.INTRO,
                    {x: characterHorizontalSpacing, y: y(.15), scale: .35, group: this.stageSelectionGroup})
                const friendCharacter = MakeFriendCharacter.add(this.game, CharacterType.FRIEND, MakeFriendProgressStore.getFriendCharacterFlavor,
                    CharacterContext.INTRO,
                    {y: y(.15), scale: .35, group: this.stageSelectionGroup})
                friendCharacter.x = Scale.width() - characterHorizontalSpacing - friendCharacter.width
        }

        const playStageSelectAudio = () => {
            const nextStageAudio = this.game.add.audio(nextStageAudioFile)
            nextStageAudio.onStop.addOnce(() => {
                if (!completedArcade) {
                    arcadeButton.inputEnabled = true
                }
                if (!completedSleepover) {
                    sleepoverButton.inputEnabled = true
                }
                if (!completedPool) {
                    poolButton.inputEnabled = true
                }
            })
            if (numCompletedStages > 2) {
                nextStageAudio.onStop.addOnce(() => {
                    this.turnOnNextButton()
                })
            }
            nextStageAudio.play()
        }

        if (isFirstStage) {
            const stageSelectionAudio = this.game.add.audio(MakeFriendAudio.WHAT_DO_YOU_AND_YOUR_FRIEND_WANT_TO_DO)
            stageSelectionAudio.onStop.addOnce(() => {
                playStageSelectAudio()
            })
            stageSelectionAudio.play()
        } else {
            playStageSelectAudio()
        }
    }
}
